'use client';
import { IconHome, IconReviewStars } from '@repo/svgs';
import { Tab } from '../ui';
import { useState, useEffect } from 'react';
import ListingsMiniSearchBar from '@/features/listings/ListingsMiniSearchBar';
import BuildingsMiniSearchBar from '@/features/buildings/BuildingsMiniSearchBar';
import { createPortal } from 'react-dom';
import ListingsSearchBar from '@/features/listings/ListingsSearchBar';
import BuildingsSearchBar from '@/features/buildings/BuildingsSearchBar';
import { AISearchBar } from '@/features/ai-search/AISearchBar';
import { AiMiniSearchBar } from '@/features/ai-search/AiMiniSearchBar';
import { useAiSearchStatus } from '@/features/ai-search/hooks/useAiSearchStatus';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useRouter } from 'next/navigation';

type ActivePanel = 'price' | 'beds' | 'location' | null;

const ANIMATION_DURATION = 500;

export const SearchBarTabs = ({
  onTabClick,
  selectedTab,
}: {
  onTabClick: (tab: 'rent' | 'reviews') => void;
  selectedTab: 'rent' | 'reviews';
}) => {
  return (
    <div className="flex items-center gap-x-4 justify-center animate-slide-down">
      <Tab
        background="secondary"
        active={selectedTab === 'rent'}
        icon={<IconHome />}
        onClick={() => onTabClick('rent')}
      >
        Rent apartments
      </Tab>

      <Tab
        background="secondary"
        active={selectedTab === 'reviews'}
        icon={<IconReviewStars />}
        onClick={() => onTabClick('reviews')}
      >
        Read reviews
      </Tab>
    </div>
  );
};

const HeaderTabbedSearchBar = ({ defaultTab = 'rent' }: { defaultTab: 'rent' | 'reviews' }) => {
  const [selectedTab, setSelectedTab] = useState(defaultTab);
  const [showTabs, setShowTabs] = useState(false);
  const [focusSearch, setFocusSearch] = useState(false);
  const [mounted, setMounted] = useState(false);
  const analytics = useAnalytics();
  const router = useRouter();
  const [activePanel, setActivePanel] = useState<ActivePanel>(null);
  const aiStatus = useAiSearchStatus();
  // AI search replaces the legacy rent mini/full search on every page that shows
  // the rent-apartments header search (only when the switch isn't disabled).
  const aiRentEnabled = defaultTab === 'rent' && aiStatus !== 'disabled';

  useEffect(() => {
    setMounted(true);
  }, []);

  const slot = mounted ? document.querySelector('#site-header-affix') : null;

  const handleTabClick = (tab: 'rent' | 'reviews') => {
    tab === 'rent'
      ? analytics.track('header_explore_listings')
      : analytics.track('header_read_review');
    setSelectedTab(tab);
  };

  const handlePanelClick = (panel: ActivePanel) => {
    setShowTabs(true);
    // Delay setting activePanel to allow animation to complete
    // The animation has delay-500 (500ms), so we wait a bit longer
    setTimeout(() => {
      setActivePanel(panel);
      // 1ms more than the animation delay-500 (500ms)
    }, ANIMATION_DURATION + 1);
  };

  const SearchBar =
    defaultTab === 'reviews' ? (
      <BuildingsMiniSearchBar
        onClick={() => setShowTabs(true)}
        onSearchClick={() => setShowTabs(true)}
        onLeftIconClick={() => setShowTabs(true)}
      />
    ) : aiRentEnabled ? (
      <AiMiniSearchBar
        onHomeClick={() => setShowTabs(true)}
        onTextClick={() => {
          setShowTabs(true);
          setFocusSearch(true);
        }}
        onSearchClick={() => router.push('/listings/status:verified')}
      />
    ) : (
      <ListingsMiniSearchBar
        onPriceClick={() => handlePanelClick('price')}
        onBedsClick={() => handlePanelClick('beds')}
        onLocationClick={() => handlePanelClick('location')}
        onLeftIconClick={() => setShowTabs(true)}
      />
    );

  if (!mounted) {
    return <div className="hidden" />;
  }

  return (
    <>
      {slot &&
        createPortal(
          showTabs ? (
            <SearchBarTabs onTabClick={handleTabClick} selectedTab={selectedTab} />
          ) : (
            SearchBar
          ),
          slot,
        )}
      {showTabs && (
        <div className="absolute top-header flex flex-col left-0 w-full h-[calc(100%-var(--spacing-header))] z-50 bg-black/20">
          <div className="flex items-center justify-center bg-primary pb-8 animate-slide-down delay-[${ANIMATION_DURATION}ms]">
            {selectedTab === 'rent' ? (
              aiRentEnabled ? (
                <div className="w-[600px] max-w-full">
                  <AISearchBar autoFocus={focusSearch} />
                </div>
              ) : (
                <ListingsSearchBar initialActivePanel={activePanel} />
              )
            ) : (
              <BuildingsSearchBar
                onBuildingSelect={() => setShowTabs(false)}
                autoFocus={selectedTab === 'reviews'}
              />
            )}
          </div>
          <div
            className="flex-1"
            onClick={() => {
              setShowTabs(false);
              setFocusSearch(false);
              setActivePanel(null);
              setSelectedTab(defaultTab);
            }}
          />
        </div>
      )}
    </>
  );
};

export default HeaderTabbedSearchBar;
